import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, TrendingUp, Users, Target } from 'lucide-react';

export const CaseStudies = () => {
  const caseStudies = [
    {
      client: 'TechFlow',
      industry: 'B2B SaaS',
      challenge: 'Despite ranking well in traditional search, TechFlow was almost invisible in ChatGPT, Perplexity, and Google AI Overviews for their core category queries.',
      solution: [
        'Restructured key product pages with enhanced schema',
        'Created AI-optimized comparison guides and use case content',
        'Launched targeted PR campaign to build citeable authority signals'
      ],
      results: [
        { metric: 'AI Citations', value: '0 to 47', icon: Target },
        { metric: 'Brand Searches', value: '+320%', icon: TrendingUp },
        { metric: 'Pipeline from AI', value: '$180K', icon: Users }
      ],
      image: 'https://images.unsplash.com/photo-1531539427495-97c44a449837?w=800&h=600&fit=crop',
      timeframe: '90 days'
    },
    {
      client: 'DataSync',
      industry: 'Data Analytics Platform',
      challenge: 'DataSync needed to establish themselves as a thought leader in AI-powered analytics before their competitors dominated the AI search space.',
      solution: [
        'Developed comprehensive AI visibility strategy',
        'Created authoritative content assets (guides, comparisons, case studies)',
        'Built relationships with industry publications for high-authority backlinks'
      ],
      results: [
        { metric: 'Perplexity Citations', value: 'Top 3', icon: Target },
        { metric: 'Lead Quality', value: '+250%', icon: TrendingUp },
        { metric: 'Demo Requests', value: '+180%', icon: Users }
      ],
      image: 'https://images.unsplash.com/photo-1622675363311-3e1904dc1885?w=800&h=600&fit=crop',
      timeframe: '120 days'
    },
    {
      client: 'CloudSecure',
      industry: 'Cybersecurity',
      challenge: 'CloudSecure was losing potential customers to competitors who were being recommended by AI engines for cybersecurity solutions.',
      solution: [
        'Optimized technical documentation and security whitepapers',
        'Implemented advanced structured data for security features',
        'Created AI-friendly FAQ and comparison content'
      ],
      results: [
        { metric: 'AI Visibility', value: '+400%', icon: Target },
        { metric: 'Organic Mentions', value: '5x', icon: TrendingUp },
        { metric: 'Enterprise Leads', value: '+150%', icon: Users }
      ],
      image: 'https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?w=800&h=600&fit=crop',
      timeframe: '6 months'
    }
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-success/10 text-success border-success/20">
            Real Results
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            Case Studies: <span className="text-gradient">Real Results</span> in AI Search
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            See how we've helped brands become referenced by AI engines—and turn that visibility into leads and revenue.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {caseStudies.map((study, index) => (
            <Card key={index} className="overflow-hidden border-border hover:shadow-xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image */}
                <div className="order-2 md:order-1">
                  <img
                    src={study.image}
                    alt={study.client}
                    className="w-full h-full object-cover min-h-[300px]"
                  />
                </div>

                {/* Content */}
                <div className="order-1 md:order-2 p-8 lg:p-12">
                  <div className="mb-4">
                    <Badge variant="outline">{study.industry}</Badge>
                  </div>
                  
                  <h2 className="text-3xl font-display font-bold mb-4">
                    {study.client}
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-display font-semibold text-lg mb-2">Challenge</h3>
                      <p className="text-muted-foreground">{study.challenge}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-display font-semibold text-lg mb-2">Solution</h3>
                      <ul className="space-y-2">
                        {study.solution.map((item, sIndex) => (
                          <li key={sIndex} className="flex items-start space-x-2">
                            <span className="text-primary mt-1">•</span>
                            <span className="text-muted-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-display font-semibold text-lg mb-4">Results ({study.timeframe})</h3>
                      <div className="grid grid-cols-3 gap-4">
                        {study.results.map((result, rIndex) => (
                          <div key={rIndex} className="text-center">
                            <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center mx-auto mb-2">
                              <result.icon className="w-5 h-5 text-primary-foreground" />
                            </div>
                            <div className="text-2xl font-display font-bold text-gradient mb-1">
                              {result.value}
                            </div>
                            <div className="text-xs text-muted-foreground">{result.metric}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Want Similar Results?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Start with a free GEO/AIO audit to see where your brand stands and what opportunities you're missing.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Get My Free Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
